import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';


const API_BASE = '/api';
const ENV_MARKER = '\n\n---\n[자동 환경정보]\n';

const readError = (e, fallback) =>
  e?.response?.data?.message || e?.message || fallback;

const splitNote = (text) => {
  const value = String(text || '');
  const index = value.indexOf(ENV_MARKER);
  if (index < 0) return { content: value, environment: '' };
  return {
    content: value.slice(0, index).trimEnd(),
    environment: value.slice(index + ENV_MARKER.length).trim(),
  };
};

const responseData = (res) => res?.data?.data || {};

export default function FarmDiaryDetailPage() {
  const { diaryId } = useParams();
  const [searchParams] = useSearchParams();
  const farmId = searchParams.get('farm_id') || '';
  const navigate = useNavigate();

  const [note, setNote] = useState(null);
  const [farm, setFarm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!farmId) {
      setError('농장 정보가 없습니다.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const [noteRes, farmRes] = await Promise.all([
        axios.get(`${API_BASE}/farms/${farmId}/diary-notes/${diaryId}`),
        axios.get(`${API_BASE}/farms/${farmId}`),
      ]);
      setNote(responseData(noteRes).note || null);
      setFarm(farmRes.data?.data?.farm || null);
    } catch (e) {
      setError(readError(e, '영농일지를 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  }, [farmId, diaryId]);

  useEffect(() => { load(); }, [load]);

  const removeDiary = async () => {
    if (!window.confirm('이 영농일지를 삭제하시겠습니까?')) return;
    try {
      await axios.delete(`${API_BASE}/farms/${farmId}/diary-notes/${diaryId}`);
      navigate(`/farm-diary/list?farm_id=${farmId}`);
    } catch (e) {
      alert(readError(e, '삭제하지 못했습니다.'));
    }
  };

  const upload = async (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;

    const formData = new FormData();
    selected.forEach((file) => formData.append('files', file));

    setUploading(true);
    try {
      await axios.post(`${API_BASE}/farms/${farmId}/notes/${diaryId}/attachments`, formData);
      await load();
    } catch (e2) {
      alert(readError(e2, '이미지를 업로드하지 못했습니다.'));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const deleteAttachment = async (filename) => {
    if (!window.confirm('이 이미지를 삭제하시겠습니까?')) return;
    try {
      await axios.delete(`${API_BASE}/farms/${farmId}/notes/${diaryId}/attachments/${encodeURIComponent(filename)}`);
      await load();
    } catch (e) {
      alert(readError(e, '이미지를 삭제하지 못했습니다.'));
    }
  };

  if (loading) return <div className="py-5 text-center"><div className="spinner-border text-primary" /></div>;
  if (error) return <div className="container py-4"><div className="alert alert-danger">{error}</div></div>;
  if (!note) return null;

  const body = splitNote(note.note);
  // 오늘 실증한 정확한 필드: note.files = ["<note_uuid>_<original_filename>", ...]
  const files = Array.isArray(note.files) ? note.files : [];
  const date = String(note.date_time || '').slice(0, 10);
  const time = String(note.date_time || '').slice(11, 16);

  return (
    <div className="container-fluid py-4 farm-diary-page" style={{ maxWidth: 1250 }}>
      <div className="d-flex flex-wrap justify-content-between gap-3 mb-4">
        <div>
          <div className="text-secondary mb-1">{date} · {farm?.name || '-'}</div>
          <h2 className="fw-bold mb-2">{note.name}</h2>
          <span className="badge text-bg-success">영농일지</span>
        </div>
        <div className="d-flex align-items-start gap-2">
          <Link className="btn btn-outline-secondary" to={`/farm-diary/list?farm_id=${farmId}`}>목록</Link>
          <Link className="btn btn-outline-success" to={`/farm-diary/edit/${diaryId}?farm_id=${farmId}`}>수정</Link>
          <button className="btn btn-outline-danger" onClick={removeDiary}>삭제</button>
        </div>
      </div>

      <div className="row g-4">
        <div className="col-12 col-xl-8">
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body p-4">
              <div className="row g-3 mb-4">
                <div className="col-6 col-md-4"><div className="text-secondary small">농장</div><div className="fw-semibold">{farm?.name || '-'}</div></div>
                <div className="col-6 col-md-4"><div className="text-secondary small">작업일</div><div className="fw-semibold">{date || '-'}</div></div>
                <div className="col-6 col-md-4"><div className="text-secondary small">작업 시간</div><div className="fw-semibold">{time || '-'}</div></div>
              </div>
              <hr />
              <h5 className="fw-bold mt-4">작업 내용</h5>
              <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>{body.content || '작성된 내용이 없습니다.'}</div>
            </div>
          </div>

          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
              <strong>첨부 이미지 {files.length}개</strong>
              <label className={`btn btn-sm btn-outline-success ${uploading ? 'disabled' : ''}`}>
                {uploading ? '업로드 중...' : '+ 이미지 추가'}
                <input hidden type="file" multiple accept="image/*" disabled={uploading} onChange={upload} />
              </label>
            </div>
            <div className="card-body">
              {files.length === 0 ? (
                <div className="text-secondary">등록된 이미지가 없습니다.</div>
              ) : (
                <div className="row g-3">
                  {files.map((filename) => {
                    const url = `${API_BASE}/farms/${farmId}/notes/${diaryId}/attachments/${encodeURIComponent(filename)}`;
                    return (
                      <div className="col-12 col-md-6" key={filename}>
                        <div className="border rounded p-2 h-100">
                          <a href={url} target="_blank" rel="noreferrer" className="d-block text-decoration-none">
                            <img src={url} alt={filename} className="img-fluid rounded w-100" style={{ maxHeight: 320, objectFit: 'contain' }} />
                          </a>
                          <div className="d-flex justify-content-between align-items-center gap-2 mt-2">
                            <a href={url} target="_blank" rel="noreferrer" className="small text-truncate">{filename}</a>
                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => deleteAttachment(filename)}>삭제</button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-4">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white py-3"><strong>환경 정보</strong></div>
            <div className="card-body">
              <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>{body.environment || '저장된 환경 정보가 없습니다.'}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
